import React from "react";
import ReactApexChart from "react-apexcharts";
import { Box } from "@mui/material";
import { ApexOptions } from "apexcharts";
import "apexcharts/dist/apexcharts.css";

interface FrequencyBand {
  title: string;
  range: string;
  suffix: string;
  specific: null | string;
  technology_id: number;
  technology_title: string;
}

interface SpectrumPricingData {
  operator?: {
    country?: {
      title?: string;
      code?: string;
    };
    title?: string;
  };
  price_total: number;
  allocated_bandwidth: number;
  price_population: number;
  country?: {
    title?: string;
    code?: string;
  };
  completed_year?: number | undefined;
  frequency_bands?: FrequencyBand[];
  children?: SpectrumPricingData[];
}

interface BubbleChartProps {
  data?: SpectrumPricingData[];
  loading?: boolean;
}

interface SeriesData {
  name: string;
  data: {
    x: number;
    y: number;
    z: number;
    originalData: SpectrumPricingData;
  }[];
}

const BubbleChart: React.FC<BubbleChartProps> = ({ data = [], loading }) => {
  // Flatten the hierarchical data structure to include children
  const flattenData = (items: SpectrumPricingData[]): SpectrumPricingData[] => {
    const flattened: SpectrumPricingData[] = [];

    items.forEach(item => {
      // Add the item itself if it has an operator
      if (item.operator && item.operator.title) {
        flattened.push(item);
      }

      // Add all children recursively
      if (item.children && item.children.length > 0) {
        flattened.push(...flattenData(item.children));
      }
    });

    return flattened;
  };

  const allData = flattenData(data);

  // Filter out parent records (where operator is null) and only use child records
  // Also include records that have an operator with a title
  const filteredData = allData.filter(item => {
    const hasOperator = item.operator && item.operator.title;

    return hasOperator;
  });

  // Check if all data is from the same country
  const uniqueCountries = new Set(filteredData.map(item => item.operator?.country?.title || item.country?.title || "Unknown"));
  const isSingleCountry = uniqueCountries.size === 1;

  // Create groups based on whether it's single country or multi-country
  const countryGroups = filteredData.reduce((acc: { [key: string]: SeriesData["data"] }, item) => {
    let groupKey: string;

    if (isSingleCountry) {
      // For single country, group by operator name
      const operatorName = item.operator?.title || "Unknown Operator";
      groupKey = operatorName;
    } else {
      // For multiple countries, group by country
      groupKey = item.operator?.country?.title || item.country?.title || "Unknown";
    }

    if (!acc[groupKey]) {
      acc[groupKey] = [];
    }

    // Store data in the format ApexCharts expects
    acc[groupKey].push({
      x: item.price_total,
      y: item.allocated_bandwidth,
      z: Math.sqrt(item.price_population), // Square root transformation
      originalData: item,
    });
    return acc;
  }, {});

  const transformedSeries = Object.entries(countryGroups).map(([groupName, points]) => ({
    name: groupName,
    data: points
      .filter(point => point.x > 0) // Filter out records with price_total = 0
      .map(point => ({
        ...point,
        x: Math.log10(point.x), // Safe to apply log10 since we filtered out zeros
      })),
  }));

  const options: ApexOptions = {
    colors: [
      "#e6194b",
      "#3cb44b",
      "#ffe119",
      "#4363d8",
      "#f58231",
      "#911eb4",
      "#46f0f0",
      "#f032e6",
      "#bcf60c",
      "#fabebe",
      "#008080",
      "#e6beff",
      "#9a6324",
      "#fffac8",
      "#800000",
      "#aaffc3",
      "#808000",
      "#ffd8b1",
      "#000075",
      "#808080",
      "#1f77b4",
      "#ff7f0e",
      "#2ca02c",
      "#d62728",
      "#9467bd",
      "#8c564b",
      "#e377c2",
      "#7f7f7f",
      "#bcbd22",
      "#17becf",
      "#aec7e8",
      "#ffbb78",
      "#98df8a",
      "#ff9896",
      "#c5b0d5",
      "#c49c94",
      "#f7b6d2",
      "#c7c7c7",
      "#dbdb8d",
      "#9edae5",
      "#393b79",
      "#637939",
      "#8c6d31",
      "#843c39",
      "#7b4173",
      "#5254a3",
      "#8ca252",
      "#bd9e39",
      "#ad494a",
      "#a55194",
      "#6b6ecf",
      "#b5cf6b",
      "#d6616b",
      "#ce6dbd",
      "#9c9ede",
      "#cedb9c",
      "#e7969c",
      "#de9ed6",
      "#3182bd",
      "#31a354",
      "#756bb1",
      "#636363",
      "#969696",
      "#9e9ac8",
      "#74c476",
      "#6baed6",
      "#fd8d3c",
      "#e6550d",
      "#a1d99b",
      "#9ecae1",
      "#dca9a9",
      "#c994c7",
      "#8c8c8c",
      "#bcbddc",
      "#bae4b3",
      "#c6dbef",
      "#fdae6b",
      "#fd8d3c",
      "#74c476",
      "#6baed6",
      "#fdd0a2",
      "#e31a1c",
      "#fb9a99",
      "#cab2d6",
      "#b2df8a",
      "#fdbf6f",
      "#ff7f00",
      "#6a3d9a",
      "#33a02c",
      "#1f78b4",
      "#a6cee3",
      "#b15928",
      "#fb8072",
      "#80b1d3",
      "#fdb462",
      "#b3de69",
      "#fccde5",
      "#bc80bd",
    ],
    chart: {
      height: 350,
      type: "bubble",
      toolbar: {
        show: true,
      },
    },
    dataLabels: {
      enabled: false,
    },
    fill: {
      opacity: 0.8,
    },
    title: {
      text: isSingleCountry ? "Spectrum Pricing Analysis - Single Country" : "Spectrum Pricing Analysis",
      align: "center",
    },
    xaxis: {
      title: {
        text: "Total Price $M",
      },
      type: "numeric",
      tickAmount: 8,
      labels: {
        formatter: (value: string) => {
          const millionValue = Math.pow(10, Number(value)) / 1_000_000;
          return `$${millionValue.toFixed(0)}M`;
        },
      },
    },
    yaxis: {
      logarithmic: true,
      title: {
        text: "Spectrum Capacity (MHz)",
      },
      //@ts-ignore
      type: "numeric",
      logBase: 10,
      tickAmount: 8,
      labels: {
        formatter: (value: string) => `${Number(value).toLocaleString()}`,
      },
    },
    tooltip: {
      custom: function ({ series, seriesIndex, dataPointIndex, w }) {
        const dataPoint = w.config.series[seriesIndex].data[dataPointIndex];
        const originalData = dataPoint.originalData;
        const countryCode = originalData.operator?.country?.code || originalData.country?.code;
        const priceInMillions = (originalData.price_total / 1000000).toFixed(2);
        const frequencyBands =
          originalData.frequency_bands?.map((band: FrequencyBand) => `${band.title} (${band.range})`).join(", ") || "N/A";

        return `
            <div class="apexcharts-tooltip-title">
              <span class="fi fi-${countryCode?.toLowerCase() ?? ""}" style="width:30px;height:23px;border:1px solid #808080;margin: 0px 7px 0px 0"></span>
              ${w.config.series[seriesIndex]?.name || "Unknown"}
            </div>
            <div class="apexcharts-tooltip-series-groupsss">
              <div class="apexcharts-tooltip-text" style="padding:5px;font-size:12px;">
                <table style="border-collapse: collapse; width: 100%;">
                  <tr>
                    <td style="padding: 4px; text-align: left;">Frequency Bands:</td>
                    <td style="padding: 4px; text-align: left;">${frequencyBands}</td>
                  </tr>
                  <tr>
                    <td style="padding: 4px; text-align: left;">Total Price:</td>
                    <td style="padding: 4px; text-align: left;">$${priceInMillions}M</td>
                  </tr>
                  <tr>
                    <td style="padding: 4px; text-align: left;">Spectrum capacity (MHz):</td>
                    <td style="padding: 4px; text-align: left;">${dataPoint.y === 0 ? "-" : dataPoint.y ? `${dataPoint.y}MHz` : "-"}</td>
                  </tr>
                  <tr>
                    <td style="padding: 4px; text-align: left;">Price/MHz/1000 pop:</td>
                    <td style="padding: 4px; text-align: left;">${Math.round(originalData.price_population * 100) === 0 ? "-" : `$${originalData.price_population.toLocaleString()}`}</td>
                  </tr>
                  <tr>
                    <td style="padding: 4px; text-align: left;">Year:</td>
                    <td style="padding: 4px; text-align: left;">${originalData.completed_year || "N/A"}</td>
                  </tr>
                </table>
              </div>
            </div>    
          `;
      },
    },
    plotOptions: {
      bubble: {
        minBubbleRadius: 5,
        maxBubbleRadius: 50,
      },
    },
  };

  if (loading) {
    return (
      <Box sx={{ width: "100%", height: "100%", minHeight: 500, marginTop: 10, marginBottom: 10 }}>
        <Box
          sx={{
            width: "100%",
            height: 500,
            border: "1px solid #eee",
            borderRadius: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          Loading...
        </Box>
      </Box>
    );
  }

  // Show message if no valid data after filtering
  if (filteredData.length === 0) {
    return (
      <Box sx={{ width: "100%", height: "100%", minHeight: 500, marginTop: 10, marginBottom: 10 }}>
        <Box
          sx={{
            width: "100%",
            height: 500,
            border: "1px solid #eee",
            borderRadius: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          No operator data available for bubble chart visualization
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", height: "100%", minHeight: 500, marginTop: 10, marginBottom: 10 }}>
      <Box sx={{ width: "100%", height: 500, border: "1px solid #eee", borderRadius: 1 }}>
        <ReactApexChart options={options} series={transformedSeries} type="bubble" height={500} width="100%" />
      </Box>
    </Box>
  );
};

export default BubbleChart;
